import { useMutation } from '@apollo/client/react';
import { Button, Card, Form, Input, Radio } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { findIndex, isNaN } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../../../AppContext';
import {
  ForwardArrow,
  Import,
  ProjectIcon,
} from '../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import {
  ACCESS_TYPE,
  CSV_SAMPLE_FILES,
  PICK_REGISTER_LABEL,
  REGISTER_STATUS_LABEL,
  ROUTES,
  TAB_KEYS,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import { formValidatorRules } from '../../../../../../common/utils';
import CommonImportModal from '../../../../../../components/CommonImportModal';
import HasAccess from '../../../../../../components/HasAccess';
import Error404 from '../../../../../../Error404';
import {
  CREATE_NEW_REGISTER,
  IMPORT_REGISTER,
} from '../../../../graphql/Mutation';
import { GET_SINGLE_PROJECT } from '../../../../graphql/Queries';

const AddEditRegister = () => {
  const { getCurrentUser, dispatch } = useContext(AppContext);
  const currentUserId = getCurrentUser()?.id || {};
  const {
    navigate,
    location,
    params: { projectId, registerId },
  } = useRouter();
  const { required, characterWithoutWhiteSpace } = formValidatorRules;
  const [form] = Form.useForm();
  const name = useWatch('name', form);
  const navFilter = location?.state?.navFilter;
  const navPagination = location?.state?.navPagination;
  const [registerName, setRegisterName] = useState();
  const [pickFrom, setPickFrom] = useState(PICK_REGISTER_LABEL.CREATE);
  const [showImportModal, setShowImportModal] = useState(false);
  const sampleFile = CSV_SAMPLE_FILES.REGISTER_CSV;
  const [registerImport, { loading: importLoading }] = useMutation(
    IMPORT_REGISTER,
    {
      fetchPolicy: 'network-only',
      onCompleted() {},
      onError() {},
    },
  );

  const [createRegister, { loading: createLoading }] = useMutation(
    CREATE_NEW_REGISTER,
    {
      fetchPolicy: 'network-only',
      onError() {},
      onCompleted(res) {
        navigate(
          `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${res?.createRegister?.id}/edit-control`,
          {
            state: {
              registerName,
            },
          },
        );
      },
    },
  );

  const [getWebProject, { data: projectData }] = useLazyQueryWithCallbacks(
    GET_SINGLE_PROJECT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        dispatch({
          type: 'SET_PROJECT_DETAILS',
          data: JSON.stringify(res?.getProject),
        });
        const index = findIndex(
          res?.getProject?.projectUsers,
          (user) => Number(user?.userId) === Number(currentUserId),
        );
        if (index !== -1) {
          const loggedInUserRole =
            res?.getProject?.projectUsers?.[index]?.roles;
          dispatch({ type: 'SET_PROJECT_ROLE', data: loggedInUserRole });
        } else {
          dispatch({ type: 'SET_PROJECT_ROLE', data: '' });
        }
      },
    },
  );

  useEffect(() => {
    if (!isNaN(Number(projectId))) {
      getWebProject({
        variables: {
          id: projectId,
        },
      });
    }
  }, [projectId]);

  if (!HasAccess({ type: ACCESS_TYPE.REGISTER })) {
    return <Error404 />;
  }

  const renderRegisterTypeUi = (type) => {
    const handleImport = () => {
      setRegisterName(name);
      setShowImportModal(true);
    };

    switch (type) {
      case PICK_REGISTER_LABEL.IMPORT:
        return (
          <>
            <Button
              shape="round"
              type="primary"
              icon={<Import />}
              onClick={handleImport}
              className="register-import-button mr-5"
              disabled={!name}
            >
              Import
            </Button>
          </>
        );

      default:
        break;
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const onFormSubmit = async (values) => {
    setRegisterName(values?.name);
    createRegister({
      variables: {
        data: {
          name,
          type: PICK_REGISTER_LABEL.CREATE,
          projectId,
          status: REGISTER_STATUS_LABEL.DRAFT,
        },
      },
    });
  };

  return (
    <div className="add-project-wrapper">
      <div className="project-details-title page-header">
        <div className="project-details-title-text d-flex align-center">
          <ProjectIcon
            onClick={() => {
              navigate(ROUTES.PROJECTS);
            }}
            className="pointer"
            id="projects-breadcrumb-icon"
          />
          <ForwardArrow />
          <h1
            className="pointer mr-10 text-primary"
            onClick={() => {
              navigate(`${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}`, {
                state: {
                  navFilter,
                  navPagination,
                },
              });
            }}
          >
            {projectData?.getProject?.name}
          </h1>
          <ForwardArrow />
          {registerId ? (
            <>
              <h1 className="pointer mr-10">{registerName}</h1>
              <ForwardArrow />
              <h1 className="mr-10 text-primary">Edit Register</h1>
            </>
          ) : (
            <h1 className="mr-10">Add Register</h1>
          )}
        </div>
      </div>
      <div>
        <div className="add-register-card-wrapper">
          <Card variant="borderless">
            <h1>Add Register</h1>
            <Form layout="vertical" form={form} onFinish={onFormSubmit}>
              <div className="mb-5">Pick From :</div>
              <Form.Item
                name="type"
                className="mb-20"
                initialValue={PICK_REGISTER_LABEL.CREATE}
              >
                <Radio.Group
                  value={pickFrom}
                  onChange={(e) => setPickFrom(e?.target?.value)}
                  size="large"
                  id="register-type-options"
                >
                  <Radio
                    value={PICK_REGISTER_LABEL.CREATE}
                    id="register-create-radio"
                  >
                    Create
                  </Radio>
                  <Radio
                    value={PICK_REGISTER_LABEL.IMPORT}
                    id="register-import-radio"
                  >
                    Import
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                rules={[
                  required,
                  characterWithoutWhiteSpace,
                  {
                    max: 250,
                    message: 'Maximum 250 characters are allowed',
                  },
                ]}
                name="name"
                label="Register Name"
              >
                <Input
                  placeholder="Enter Name"
                  allowClear
                  id="register-name-input"
                />
              </Form.Item>
              {renderRegisterTypeUi(pickFrom)}
              <div className="form-buttons">
                <Button
                  shape="round"
                  className="cancel-button"
                  onClick={handleCancel}
                  id="add-register-cancel-btn"
                >
                  Cancel
                </Button>
                {pickFrom !== PICK_REGISTER_LABEL.IMPORT && (
                  <Button
                    shape="round"
                    className="save-button"
                    type="primary"
                    htmlType="submit"
                    loading={createLoading}
                    id="add-register-save-btn"
                  >
                    Save
                  </Button>
                )}
              </div>
            </Form>
          </Card>
        </div>
      </div>
      {showImportModal && (
        <CommonImportModal
          showImportModal={showImportModal}
          setShowImportModal={setShowImportModal}
          title="Register"
          sampleFileUrl={sampleFile}
          list={['Register Name', 'Field Type', 'Type', 'Is Required']}
          nonMandatoryList={['Options']}
          updateRegisterImport={registerImport}
          importLoading={importLoading}
          filePrefix="registerCsv"
          additionalPoints={[
            'Name field should be unique.',
            'Calculation type is not allowed during import.',
          ]}
          registerName={registerName}
          projectId={projectId}
        />
      )}
    </div>
  );
};

export default AddEditRegister;
